import { useState, useCallback } from 'react';
import { Droplets, Check, Info } from 'lucide-react';
import LabBench from '../../LabBench';
import { Dropper } from '@/components/lab';
import InfoBox from '../../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, {
  title: string;
  content: string;
  whatHappens: string;
  observe: string[];
  equation?: string;
}> = {
  1: {
    title: 'Preparing the Silver Solution',
    content: 'Silver nitrate (AgNO₃) dissolves in water to form colorless Ag⁺ and NO₃⁻ ions. Silver ions are one of the few cations that form insoluble chlorides. This property makes chloride precipitation the definitive test for silver ions.',
    whatHappens: 'You are adding 3 mL of Ag⁺ solution to a clean test tube. The solution is colorless because the Ag⁺ ion has a full d10 electron configuration and does not absorb visible light.',
    observe: [
      'Solution is completely colorless',
      'Solution should be clear, not cloudy',
      'Keep solution away from light (Ag compounds are photosensitive)'
    ]
  },
  2: {
    title: 'Adding Hydrochloric Acid',
    content: 'Hydrochloric acid (HCl) provides chloride ions (Cl-) that react with silver ions to form silver chloride (AgCl). AgCl has an extremely low solubility product (Ksp = 1.77 x 10^-10), meaning even tiny amounts of Ag⁺ and Cl⁻ will precipitate.',
    whatHappens: 'When HCl is added, an immediate white precipitate of AgCl forms. This is AgCl (silver chloride), which is so insoluble that even dilute HCl causes complete precipitation of silver ions.',
    observe: [
      'Immediate formation of white precipitate',
      'White precipitate of AgCl (silver chloride)',
      'Precipitate settles slowly due to low density'
    ],
    equation: 'Ag⁺(aq) + Cl⁻(aq) → AgCl(s) ↓ (white precipitate)'
  },
  3: {
    title: 'Observing the AgCl Precipitate',
    content: 'Silver chloride (AgCl) is white when freshly precipitated but darkens when exposed to light due to photoreduction to metallic silver. This white precipitate is characteristic of AgCl and distinguishes it from other precipitates.',
    whatHappens: 'You observe the characteristic white precipitate of AgCl (silver chloride). In bright light, you may see a slight graying at the surface due to photoreduction.',
    observe: [
      'White precipitate of AgCl (silver chloride)',
      'May darken with light exposure',
      'Precipitate does not dissolve with more HCl'
    ]
  },
  4: {
    title: 'Recording the Positive Result',
    content: 'The formation of a white precipitate of AgCl with HCl is a POSITIVE confirmatory test for silver ions. This test is highly selective because AgCl is one of the few metal chlorides that is insoluble. Lead(II) chloride is slightly soluble and forms a different precipitate type.',
    whatHappens: 'You record that the test is positive for Ag⁺. The white precipitate of AgCl (silver chloride) is definitive evidence for the presence of silver ions.',
    observe: [
      'Positive result: white precipitate of AgCl confirms Ag⁺',
      'This test is specific to silver (and partially Pb²⁺)',
      'Record observations in your lab notebook'
    ],
    equation: 'Ag⁺ + Cl⁻ → AgCl↓ (white precipitate, Ksp = 1.77 × 10^-10)'
  }
};

interface Part3SilverTestProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part3SilverTest({ onStepComplete, currentStep = 1 }: Part3SilverTestProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [agSolutionAdded, setAgSolutionAdded] = useState(false);
  const [hclDrops, setHclDrops] = useState(0);
  const [isDropping, setIsDropping] = useState(false);
  const [precipitateObserved, setPrecipitateObserved] = useState(false);
  const [resultRecorded, setResultRecorded] = useState(false);

  const getFillLevel = () => {
    if (!agSolutionAdded) return 0;
    return 50 + hclDrops * 3;
  };

  const getPrecipitateAmount = () => {
    return Math.min(hclDrops * 15, 100);
  };

  const handleAddAgSolution = useCallback(() => {
    setAgSolutionAdded(true);
    if (onStepComplete) onStepComplete('silver-1');
  }, [onStepComplete]);

  const handleAddHCl = useCallback(() => {
    if (agSolutionAdded && hclDrops < 5) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setHclDrops(prev => {
          const newDrops = prev + 1;
          if (newDrops >= 3 && onStepComplete) {
            onStepComplete('silver-2');
          }
          return newDrops;
        });
      }, 500);
    }
  }, [agSolutionAdded, hclDrops, onStepComplete]);

  const handleObservePrecipitate = useCallback(() => {
    if (hclDrops >= 3) {
      setPrecipitateObserved(true);
      if (onStepComplete) onStepComplete('silver-3');
    }
  }, [hclDrops, onStepComplete]);

  const handleRecordResult = useCallback(() => {
    if (precipitateObserved) {
      setResultRecorded(true);
      if (onStepComplete) onStepComplete('silver-4');
    }
  }, [precipitateObserved, onStepComplete]);

  return (
    <LabBench
      experimentName="Silver Confirmation Test"
      completedCount={[agSolutionAdded, hclDrops >= 3, precipitateObserved, resultRecorded].filter(Boolean).length}
      totalSteps={4}
      equipment={['Ag⁺ solution', '6M HCl', 'Test tube']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="silver-test">
        {/* Equipment Area */}
        <div className="equipment-row">
          {/* Test Tube with Ag+ solution and precipitate */}
          <div className="equipment-section">
            <svg viewBox="0 0 70 140" width={70} height={140}>
              <defs>
                <linearGradient id="tube-glass-ag" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
                <linearGradient id="ag-liquid" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#F5F5F5" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="#EEEEEE" stopOpacity="0.95" />
                </linearGradient>
              </defs>

              {/* Test tube rim */}
              <ellipse cx="35" cy="15" rx="16" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Test tube body */}
              <path d="M19 15 L19 105 Q19 120 35 120 Q51 120 51 105 L51 15" fill="url(#tube-glass-ag)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Liquid fill */}
              {agSolutionAdded && (
                <path
                  d={`M21 ${120 - getFillLevel()} L21 103 Q21 117 35 117 Q49 117 49 103 L49 ${120 - getFillLevel()} Z`}
                  fill="url(#ag-liquid)"
                />
              )}

              {/* White curdy AgCl precipitate */}
              {hclDrops > 0 && (
                <g>
                  {/* Main precipitate mass */}
                  <ellipse
                    cx="35"
                    cy="100"
                    rx={8 + getPrecipitateAmount() / 10}
                    ry={3 + getPrecipitateAmount() / 20}
                    fill="#FAFAFA"
                    stroke="#E0E0E0"
                    strokeWidth="0.5"
                  />

                  {/* Curdy chunks floating */}
                  {hclDrops >= 2 && (
                    <>
                      <ellipse cx="28" cy="85" rx="4" ry="2" fill="#FFFAFA" stroke="#E8E8E8" strokeWidth="0.3">
                        <animate attributeName="cy" values="85;90;85" dur="3s" repeatCount="indefinite" />
                      </ellipse>
                      <ellipse cx="42" cy="80" rx="3" ry="1.5" fill="#FFFAFA" stroke="#E8E8E8" strokeWidth="0.3">
                        <animate attributeName="cy" values="80;88;80" dur="2.5s" repeatCount="indefinite" />
                      </ellipse>
                      <ellipse cx="35" cy="75" rx="5" ry="2" fill="#FFFAFA" stroke="#E8E8E8" strokeWidth="0.3">
                        <animate attributeName="cy" values="75;82;75" dur="2.8s" repeatCount="indefinite" />
                      </ellipse>
                    </>
                  )}

                  {/* More precipitate with more drops */}
                  {hclDrops >= 4 && (
                    <>
                      <ellipse cx="30" cy="95" rx="6" ry="3" fill="#FFFAFA" />
                      <ellipse cx="40" cy="93" rx="5" ry="2.5" fill="#FFFAFA" />
                    </>
                  )}
                </g>
              )}

              {/* Glass shine */}
              <line x1="23" y1="20" x2="23" y2="95" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="equipment-label">Ag⁺ Solution</span>
          </div>

          {/* HCl Dropper */}
          <div className="equipment-section">
            <Dropper
              label="6M HCl"
              liquidColor="#F5F5F5"
              onClick={agSolutionAdded && !resultRecorded ? handleAddHCl : undefined}
              active={agSolutionAdded && hclDrops < 5}
              dropping={isDropping}
              disabled={!agSolutionAdded || hclDrops >= 5}
            />
          </div>
        </div>

        {/* Status Messages */}
        <div className="status-section">
          {!agSolutionAdded && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 1</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
                >
                  ?
                </button>
              </div>
              <p>Add 3 mL of <strong>Ag⁺ solution</strong> to a clean test tube.</p>
              <button className="action-btn" onClick={handleAddAgSolution}>
                <Droplets size={16} />
                Add Ag⁺ Solution
              </button>
            </div>
          )}

          {agSolutionAdded && hclDrops < 3 && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 2</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(2); }}
                >
                  ?
                </button>
              </div>
              <p>Add <strong>2-3 drops of 6M HCl</strong>. ({hclDrops}/3+ drops)</p>
              <p className="hint">Click the dropper to add HCl drops. Watch for the white precipitate!</p>
            </div>
          )}

          {hclDrops >= 3 && !precipitateObserved && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 3</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(3); }}
                >
                  ?
                </button>
              </div>
              <p>Observe the <strong>white precipitate of AgCl</strong> (silver chloride).</p>
              <button className="action-btn" onClick={handleObservePrecipitate}>
                <Check size={16} />
                Observe Precipitate
              </button>
            </div>
          )}

          {precipitateObserved && !resultRecorded && (
            <div className="instruction-card success">
              <div className="instruction-header">
                <span className="step-badge">Step 4</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(4); }}
                >
                  ?
                </button>
              </div>
              <p>Record the <strong>positive result</strong> for silver confirmation.</p>
              <button className="action-btn success" onClick={handleRecordResult}>
                <Check size={16} />
                Record Result
              </button>
            </div>
          )}

          {resultRecorded && (
            <div className="result-card">
              <Check size={20} />
              <div>
                <p><strong>Ag⁺ Confirmed!</strong></p>
                <p>White precipitate of AgCl (silver chloride) confirms the presence of silver ions.</p>
              </div>
            </div>
          )}
        </div>

        {/* Precipitate indicator */}
        {hclDrops > 0 && (
          <div className="precipitate-indicator">
            <span>Precipitate Formation:</span>
            <div className="precipitate-bar">
              <div
                className="precipitate-fill"
                style={{ width: `${getPrecipitateAmount()}%` }}
              />
            </div>
            <span className="precipitate-label">
              {getPrecipitateAmount() < 30 ? 'Forming...' :
               getPrecipitateAmount() < 70 ? 'Increasing' : 'Complete'}
            </span>
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4><Info size={14} /> Silver Chloride Precipitation</h4>
          <p>
            Silver ions react instantly with chloride ions to form highly insoluble
            silver chloride (AgCl). The white precipitate of AgCl is characteristic of
            this reaction and serves as a definitive test for silver ions.
          </p>
          <div className="equation">
            Ag<sup>+</sup>(aq) + Cl<sup>-</sup>(aq) → AgCl(s) ↓ (white)
          </div>
          <p className="equation-note">K<sub>sp</sub> of AgCl = 1.77 × 10<sup>-10</sup> (extremely insoluble)</p>
        </div>

        <style>{`
          .silver-test {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 600px;
          }

          .equipment-row {
            display: flex;
            gap: 50px;
            align-items: flex-end;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .status-section {
            width: 100%;
            min-height: 100px;
          }

          .instruction-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .instruction-card.success {
            border-left-color: #00A86B;
          }

          .step-badge {
            display: inline-block;
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .instruction-card p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .instruction-card .hint {
            font-size: 12px;
            color: var(--text-muted);
            font-style: italic;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            margin-top: 8px;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .result-card {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px 20px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 12px;
            color: #00A86B;
          }

          .result-card p {
            margin: 0;
            color: var(--text-primary);
          }

          .result-card p:first-child {
            color: #00A86B;
            font-weight: 600;
          }

          .precipitate-indicator {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 12px;
            width: 100%;
          }

          .precipitate-bar {
            flex: 1;
            height: 8px;
            background: var(--bg-dark);
            border-radius: 4px;
            overflow: hidden;
          }

          .precipitate-fill {
            height: 100%;
            background: linear-gradient(90deg, #E0E0E0, #FAFAFA);
            transition: width 0.3s;
            border-radius: 4px;
          }

          .precipitate-label {
            color: var(--text-muted);
            min-width: 70px;
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .theory-box .equation {
            text-align: center;
            font-size: 14px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            background: var(--bg-dark);
            padding: 10px;
            border-radius: 6px;
            margin-bottom: 8px;
          }

          .equation-note {
            font-size: 11px !important;
            color: var(--text-muted) !important;
            text-align: center;
          }

          .instruction-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp2"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
